import { ReactElement, useContext, useState } from "react";
import { IconButton, Paper, Tooltip } from "@mui/material";
import { SettingsApplications } from "@mui/icons-material";
import { ComponentType, IDashboard, IDashboardComponent, IGripPosition } from "../types";
import { SystemContext } from "../../../context/systemContext";
import { getGripLocation } from "../utils";
import AddComponent from "./addComponent";
import EditComponentTool from "./editComponentTool";
import EventComponent from "./widgets/events/eventComponent";
import IndicatorComponent from "./widgets/indicators/indicatorComponent";
import AcquisitionChartWrapper from "./widgets/chart/acquisitionChartWrapper";
import DeleteIcon from "@mui/icons-material/Delete";
import RadarWidget from "./widgets/radar/radarWidget";
import DoughnutWrapper from "./widgets/doughnut/doughnutWrapper";
import GaugeWrapper from "./widgets/gauge/gaugeWrapper";
import TextComponent from "./widgets/text/textComponent";
import CustomWidgetWrapper from "./widgets/customWidgets/customWidgetWrapper";
import { DashboardSchemaType } from "../../interruptions/types";
import useDimensions from "../../../system/useDimensions";

interface IProps {
  realtime: Record<string, any>;
  component: IDashboardComponent<any>;
  loading: boolean;
  dashboard: IDashboard;
  setDashboard: (dashboard: IDashboard) => void;
  disableSettings: boolean;
  runtime: boolean;
  setPanelPosition: (position: IGripPosition | null) => void;
  selectedComponent: string | null;
  seSelectedComponent: (key: string | null) => void;
  schemas: Record<DashboardSchemaType, any>;
}

const Component = ({ component, dashboard, setDashboard, disableSettings, runtime, realtime, setPanelPosition, seSelectedComponent, selectedComponent, schemas }: IProps): ReactElement => {
  const systemContext  = useContext(SystemContext);
  const [ showSettings, setShowSettings ] = useState<boolean>(false);
  const [ref, dimensions] = useDimensions<HTMLDivElement>();

  const onSettingsClose = () => {
    setShowSettings(false);
    setPanelPosition(null);
  };

  const onSettingsAccept = (update: IDashboardComponent<any>) => {
    const newDashboard = { ...dashboard };
    newDashboard.layout.components = newDashboard.layout.components.map(c => c.key === component.key ? update : c);
    setDashboard(newDashboard);

    setShowSettings(false);
    setPanelPosition(null);
  };

  const removeComponent =  () => {
    systemContext.showMessage({
      title: "Delete Component",
      color: "danger",
      message: "Do you want to delete the Component?",
      callback: () =>{
        const newDashboard = { ...dashboard };
        newDashboard.layout.components = newDashboard.layout.components.filter(c => c.key !== component.key);
        setDashboard(newDashboard);
      }
    });
  };

  const editAllowed = dashboard.userID === 0 || systemContext.user?.userID === dashboard.userID || dashboard.isEditable;

  return (
    <Paper className="display-grid component-tool-parent" onClick={() => seSelectedComponent(component.key)}
      style={{ position: "relative",
        ...getGripLocation(component),
        gridTemplateColumns: "1fr",
        gridTemplateRows: "auto 1fr",
        zIndex: selectedComponent === component.key ? 100 : 0,
        background: component.background
      }}>

      <div className="display-flex-row flex-nowrap">
        { component.title !== "" && component.componentType !== ComponentType.Text &&
          <strong className="flex-grow-1" style={{ marginLeft: 6, color: component.foreground }}>{component.title}</strong>
        }
      </div>

      <div className="justify-self-stretch align-self-stretch " style={{ position: "relative", overflow: "hidden" }} ref={ref}>
        {component.componentType === ComponentType.Indicators &&
          <IndicatorComponent component={component} realtime={realtime} />
        }
        {component.componentType === ComponentType.Events &&
          <EventComponent component={component} realtime={realtime} dashboard={dashboard}/>
        }
        {component.componentType === ComponentType.Radar &&
          <RadarWidget component={component} realtime={realtime} parentSize={{ width: dimensions.width, height: dimensions.height - 10 }}/>
        }
        {component.componentType === ComponentType.AcquisitionChart &&
          <AcquisitionChartWrapper realtime={realtime} component={component} dashboard={dashboard} runtime={runtime}
            parentSize={{ width: dimensions.width, height: dimensions.height - 10 }} setDashboard={setDashboard} schemas={schemas} />
        }
        {component.componentType === ComponentType.Doughnut &&
          <DoughnutWrapper component={component} realtime={realtime} parentSize={{ width: dimensions.width, height: dimensions.height - 10 }}/>
        }
        {component.componentType === ComponentType.Gauge &&
          <GaugeWrapper component={component} realtime={realtime} parentSize={{ width: dimensions.width, height: dimensions.height - 10 }}/>
        }
        {component.componentType === ComponentType.Text &&
          <TextComponent component={component} />
        }
        {component.componentType === ComponentType.Custom &&
          <CustomWidgetWrapper component={component} parentSize={{ width: dimensions.width, height: dimensions.height - 10 }} schemas={schemas} />
        }
      </div>

      {!disableSettings && !runtime &&
        <div className="component-tool" style={{  position: "absolute", top: 0, bottom: 0, left: 0, right: 0, background: "rgba(0, 0, 0, .5)", zIndex: 9999,  borderRadius: 4, overflow: "hidden" }}>
          <div className="display-flex-row flex-grow-0 " style={{ position: "absolute", top: 0, left: 0, borderRadius: 4, background: "rgba(0, 0, 0, .5)" }}>
            <IconButton style={{ marginRight: "6px" }} color="primary"
              onClick={() => setShowSettings(true)} disabled={!editAllowed}>
              <Tooltip title={"Config"}>
                <SettingsApplications/>
              </Tooltip>
            </IconButton>

            <IconButton sx={{ marginRight: "6px" }} color="error" onClick={removeComponent}>
              <Tooltip title={"Remove"}>
                <DeleteIcon/>
              </Tooltip>
            </IconButton>
          </div>

          <EditComponentTool component={component} updateComponent={onSettingsAccept}/>
        </div>
      }

      {showSettings &&
        <AddComponent component={component} onClose={onSettingsClose} onAccept={onSettingsAccept}
          setPanelPosition={setPanelPosition}/>
      }
    </Paper>
  );
};

export default Component;
